import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import {
  Building2, ChevronDown, ChevronRight, Folder, Loader2, Search, User, Users, X,
} from '../../icons';
import { Avatar } from '../shared/Avatar';
import {
  getTeam, listAllTeams, listDirectMembers, listDivisions,
  type DivisionDto, type TeamDto,
} from '../../api/organization';
import { useOrgDirectoryStore } from '../../store/orgDirectoryStore';
import { avatarImageUrl } from '../../lib/avatar-src';
import { PersonDetailPopup, type OrgPerson } from '../PersonDetailPopup';
import './OrgDirectoryModal.css';

/**
 * 조직도 내부 팝업 (design 2026-08-17 확정 시안).
 *
 * 구성 = 검색줄(닫기 포함) + 트리 두 줄. 제목줄·인원수 배지·체크박스·선택바는 두지 않는다.
 *   회사 → (회사 직속) → 부서 → 팀 → 직원 순, 이름 바로 옆에 인원 수.
 * 사람을 누르면 **모달 오른쪽에 붙는 팝업**이 열려
 *   왼쪽 = 프로필 · 근무/출근 · 채팅하기 · 메일쓰기 · 일정보기, 오른쪽 = 대상자 정보.
 *
 * 데이터는 열 때 한 번에 모은다(부서·팀·직속 + 모든 팀의 멤버). 검색이 접힌 노드 안까지
 * 훑어야 해서 지연 로딩으로는 결과가 비어 보이기 때문이다. 팀 수만큼 요청이 나가므로
 * 팀이 아주 많은 조직에서는 집계 엔드포인트로 대체하는 것이 맞다(후속).
 */

interface TeamNode {
  team: TeamDto;
  members: OrgPerson[];
}
interface DivisionNode {
  division: DivisionDto;
  teams: TeamNode[];
}
interface OrgTree {
  direct: OrgPerson[];
  divisions: DivisionNode[];
  looseTeams: TeamNode[];
}

const EMPTY_TREE: OrgTree = { direct: [], divisions: [], looseTeams: [] };

/** 팝업 치수 — CSS 가 아니라 여기서 잡는다(위치 계산이 픽셀 단위라 한곳에 모아야 어긋나지 않는다). */
const TREE_W = 500;
const TREE_MAX_H = 632;
const DETAIL_W = 560;
/** 앵커를 못 받은 경우(키보드 진입 등) 좌측 레일 바로 오른쪽. */
const RAIL_FALLBACK_X = 82;

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);

/** 검색 대상 — 서버가 주는 값만. 직급·내선은 컬럼이 없어 아직 못 건다. */
function haystack(p: OrgPerson): string {
  return [
    // ★ 휴대전화·사번은 이제 조직 응답에 없다(판 3) — 옛 서버가 보내면 그때만 걸린다.
    p.name, p.position, p.duty, p.email, p.phone, p.employeeNumber,
    p.divisionName, p.teamName,
  ].filter(Boolean).join(' ').toLowerCase();
}

/** 검색어에 걸린 부분만 <mark> — 강조가 목적이라 첫 일치 한 곳만 감싼다. */
function Highlight({ text, query }: { text: string; query: string }) {
  if (!query || !text) return <>{text}</>;
  const i = text.toLowerCase().indexOf(query);
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <mark className="orgdir-hl">{text.slice(i, i + query.length)}</mark>
      {text.slice(i + query.length)}
    </>
  );
}

function Count({ n }: { n: number }) {
  return (
    <span className="orgdir-counts">
      <span className="orgdir-cnt" title={`인원 ${n}명`}>
        <User size={11} aria-hidden />{n}
      </span>
    </span>
  );
}

export function OrgDirectoryModal() {
  const open = useOrgDirectoryStore((s) => s.open);
  const close = useOrgDirectoryStore((s) => s.close);
  const anchor = useOrgDirectoryStore((s) => s.anchor);

  const [tree, setTree] = useState<OrgTree>(EMPTY_TREE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set(['co']));
  const [query, setQuery] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);

  /** 조회를 이미 시작했는가 — 렌더와 무관한 사실이라 state 가 아니라 ref. */
  const startedRef = useRef(false);

  // 열 때 한 번 읽는다. 닫아도 유지 → 다시 열면 즉시 보인다(재요청은 없음).
  //
  // ⚠ 의존성에 loading/tree 를 넣으면 안 된다 — setLoading(true) 가 곧바로 **자기 cleanup 을
  //   돌려** 진행 중인 요청을 alive=false 로 버린다(화면이 "불러오는 중" 에서 멈춘다).
  //   "이미 시작했는가" 는 렌더와 무관한 사실이므로 ref 로 기억한다.
  useEffect(() => {
    if (!open || startedRef.current) return;
    startedRef.current = true;
    let alive = true;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const [divisions, allTeams, direct] = await Promise.all([
          listDivisions(), listAllTeams(), listDirectMembers(),
        ]);
        const details = await Promise.all(
          allTeams.map(async (t) => {
            try {
              const d = await getTeam(t.id);
              return { team: t, members: d.members };
            } catch {
              return { team: t, members: [] };
            }
          }),
        );
        if (!alive) return;

        const nodeOf = (t: TeamDto & { divisionName?: string | null }): TeamNode => {
          const found = details.find((d) => d.team.id === t.id);
          const divisionName = t.divisionName
            ?? divisions.find((d) => d.id === t.divisionId)?.name
            ?? null;
          return {
            team: t,
            members: (found?.members ?? []).map((m) => ({
              ...m.user,
              divisionName,
              teamName: t.name,
              duty: m.isLeader ? '팀장' : null,
            })),
          };
        };

        setTree({
          direct: direct.map((u) => ({ ...u, divisionName: null, teamName: null, duty: null })),
          divisions: divisions.map((div) => ({
            division: div,
            teams: allTeams.filter((t) => t.divisionId === div.id).map(nodeOf),
          })),
          looseTeams: allTeams.filter((t) => t.divisionId === null).map(nodeOf),
        });
        // 회사와 부서까지는 펼친 채로 연다 — 팀 목록이 바로 보여야 사람까지 한 번에 닿는다.
        // (팀은 접어 둔다. 다 펼치면 큰 조직에서 첫 화면이 사람 목록으로 뒤덮인다.)
        setExpanded(new Set(['co', ...divisions.map((d) => d.id)]));
      } catch (err) {
        if (alive) {
          startedRef.current = false;   // 실패는 다시 열 때 재시도할 수 있게 되돌린다
          setError((err as Error)?.message ?? '조직 조회에 실패했습니다');
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open]);

  // 화면 크기 — 팝업 위치를 픽셀로 계산하므로 창이 바뀌면 다시 그린다.
  const [vp, setVp] = useState(() => ({
    w: typeof window === 'undefined' ? 1280 : window.innerWidth,
    h: typeof window === 'undefined' ? 800 : window.innerHeight,
  }));
  useEffect(() => {
    if (!open) return;
    const onResize = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open]);

  // 바깥을 누르면 닫는다 — 덮는 막이 없으니 이 리스너가 그 역할을 한다.
  // 좌측 레일(.navbar)은 제외한다: 조직도 항목이 토글이라, 여기서 먼저 닫으면 곧바로 다시 열린다.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (t.closest('.orgdir-modal') || t.closest('.orgdir-detail') || t.closest('.navbar')) return;
      close();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, close]);

  // ESC — 상세가 열려 있으면 상세만 닫고, 아니면 팝업을 닫는다(한 번에 다 닫히지 않게).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (activeId) setActiveId(null);
      else close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, activeId, close]);

  const q = query.trim().toLowerCase();

  /** 검색으로 걸러낸 트리. 부서/팀 이름이 맞으면 그 아래를 통째로 남긴다. */
  const view = useMemo<OrgTree>(() => {
    if (!q) return tree;
    const keepTeam = (n: TeamNode, divHit: boolean): TeamNode | null => {
      const teamHit = divHit || n.team.name.toLowerCase().includes(q);
      const members = teamHit ? n.members : n.members.filter((m) => haystack(m).includes(q));
      return teamHit || members.length ? { team: n.team, members } : null;
    };
    return {
      direct: tree.direct.filter((m) => haystack(m).includes(q)),
      divisions: tree.divisions
        .map((d) => {
          const divHit = d.division.name.toLowerCase().includes(q);
          const teams = d.teams.map((t) => keepTeam(t, divHit)).filter((t): t is TeamNode => !!t);
          return divHit || teams.length ? { division: d.division, teams } : null;
        })
        .filter((d): d is DivisionNode => !!d),
      looseTeams: tree.looseTeams.map((t) => keepTeam(t, false)).filter((t): t is TeamNode => !!t),
    };
  }, [tree, q]);

  const everyone = useMemo<OrgPerson[]>(() => [
    ...tree.direct,
    ...tree.divisions.flatMap((d) => d.teams.flatMap((t) => t.members)),
    ...tree.looseTeams.flatMap((t) => t.members),
  ], [tree]);

  const active = useMemo(() => everyone.find((p) => p.id === activeId) ?? null, [everyone, activeId]);

  const shownCount = view.direct.length
    + view.divisions.reduce((n, d) => n + d.teams.reduce((m, t) => m + t.members.length, 0), 0)
    + view.looseTeams.reduce((n, t) => n + t.members.length, 0);
  const totalCount = everyone.length;

  const isOpen = useCallback((key: string) => expanded.has(key) || !!q, [expanded, q]);
  const toggle = useCallback((key: string) => {
    setExpanded((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  }, []);

  // ── 위치 ──────────────────────────────────────────────────────────
  // 트리는 **누른 메뉴 옆**에, 상세는 **화면 가운데**에. 둘 다 화면 안에 가둔다.
  const MARGIN = 8;
  const treeW = Math.min(TREE_W, vp.w - MARGIN * 2);
  const treeH = Math.min(TREE_MAX_H, Math.round(vp.h * 0.86));
  const treeLeft = clamp(
    anchor ? anchor.right + 10 : RAIL_FALLBACK_X,
    MARGIN,
    Math.max(MARGIN, vp.w - treeW - MARGIN),
  );
  // 조직도는 레일 아래쪽에 있다 — 버튼 아래끝에 팝업 아래끝을 맞춰 위로 펼친다.
  const treeTop = clamp(
    anchor ? anchor.bottom - treeH : Math.round((vp.h - treeH) / 2),
    MARGIN,
    Math.max(MARGIN, vp.h - treeH - MARGIN),
  );
  const treeStyle: CSSProperties = { left: treeLeft, top: treeTop, width: treeW, height: treeH };

  // 상세는 가운데. 다만 트리를 덮지 않게 오른쪽으로만 밀어준다(자리가 없으면 그대로 가운데).
  const detailW = Math.min(DETAIL_W, vp.w - MARGIN * 2);
  const centered = Math.round((vp.w - detailW) / 2);
  const rightOfTree = treeLeft + treeW + 12;
  const detailLeft = clamp(
    rightOfTree + detailW + MARGIN <= vp.w ? Math.max(centered, rightOfTree) : centered,
    MARGIN,
    Math.max(MARGIN, vp.w - detailW - MARGIN),
  );
  const detailStyle: CSSProperties = { left: detailLeft, width: detailW };

  if (!open) return null;

  const nothingFound = !!q && shownCount === 0
    && view.divisions.length === 0 && view.looseTeams.length === 0;

  const renderTeam = (n: TeamNode, indent: number) => (
    <div key={n.team.id}>
      <div className="orgdir-row orgdir-row-team" style={{ paddingLeft: indent }}>
        <button
          type="button"
          className="orgdir-rowmain"
          aria-expanded={isOpen(n.team.id)}
          onClick={() => toggle(n.team.id)}
        >
          <span className="orgdir-twisty">
            {isOpen(n.team.id) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </span>
          <span className="orgdir-nodeicon"><Users size={14} aria-hidden /></span>
          <span className="orgdir-nodename"><Highlight text={n.team.name} query={q} /></span>
          <Count n={n.members.length} />
        </button>
      </div>
      {isOpen(n.team.id) && n.members.map((m) => renderPerson(m, indent + 22))}
    </div>
  );

  const renderPerson = (p: OrgPerson, indent: number) => (
    <div
      key={p.id}
      className={`orgdir-person${activeId === p.id ? ' is-active' : ''}`}
      style={{ paddingLeft: indent }}
    >
      <button
        type="button"
        className="orgdir-personmain"
        aria-pressed={activeId === p.id}
        onClick={() => setActiveId(p.id)}
      >
        <Avatar userId={Number(p.id) || 0} name={p.name} src={avatarImageUrl(p.id, p.avatarUrl)} size="xs" />
        <span className="orgdir-ptext">
          <span className="orgdir-pname">
            <Highlight text={p.name} query={q} />
            {p.duty && <span className="orgdir-duty">{p.duty}</span>}
          </span>
          {/* 소속이 없으면 그 자리를 비운다 — 없다는 사실을 굳이 말로 채우지 않는다. */}
          <span className="orgdir-pmeta">
            {p.position && <Highlight text={p.position} query={q} />}
            {p.position && p.teamName && ' · '}
            {p.teamName && <Highlight text={p.teamName} query={q} />}
          </span>
        </span>
      </button>
    </div>
  );

  return createPortal(
    <>
      {/* 화면을 덮는 막(scrim)을 두지 않는다 — 뒤 화면이 그대로 보이고 그대로 눌린다. */}
      <div className="orgdir-modal" style={treeStyle} role="dialog" aria-label="조직도">
          <div className="orgdir-search">
            <label className="orgdir-searchbox">
              <Search size={15} aria-hidden />
              <input
                type="search"
                value={query}
                autoComplete="off"
                aria-label="구성원 검색"
                placeholder="이름 · 직위 · 직책 · 부서 · 전화 · 아이디"
                onChange={(e) => setQuery(e.target.value)}
              />
            </label>
            <button type="button" className="orgdir-iconbtn" aria-label="닫기" onClick={close}>
              <X size={15} />
            </button>
          </div>

          <div className="orgdir-tree" role="tree" aria-label="조직 트리">
            {loading && (
              <div className="orgdir-note"><Loader2 size={14} className="orgdir-spin" /> 불러오는 중…</div>
            )}
            {error && <div className="orgdir-note orgdir-note-error" role="alert">{error}</div>}

            {!loading && !error && (
              <>
                <div className="orgdir-row orgdir-row-company" style={{ paddingLeft: 12 }}>
                  <button
                    type="button"
                    className="orgdir-rowmain"
                    aria-expanded={isOpen('co')}
                    onClick={() => toggle('co')}
                  >
                    <span className="orgdir-twisty">
                      {isOpen('co') ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </span>
                    <span className="orgdir-nodeicon"><Building2 size={15} aria-hidden /></span>
                    <span className="orgdir-nodename">우리 회사</span>
                    <Count n={q ? shownCount : totalCount} />
                  </button>
                </div>

                {isOpen('co') && (
                  <>
                    {/* 회사 직속 — 부서보다 먼저, 부서와 같은 깊이. 팀이 없는 대표이사 자리. */}
                    {view.direct.map((p) => renderPerson(p, 24))}

                    {view.divisions.map((d) => (
                      <div key={d.division.id}>
                        <div className="orgdir-row orgdir-row-division" style={{ paddingLeft: 24 }}>
                          <button
                            type="button"
                            className="orgdir-rowmain"
                            aria-expanded={isOpen(d.division.id)}
                            onClick={() => toggle(d.division.id)}
                          >
                            <span className="orgdir-twisty">
                              {isOpen(d.division.id) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </span>
                            <span className="orgdir-nodeicon"><Folder size={14} aria-hidden /></span>
                            <span className="orgdir-nodename"><Highlight text={d.division.name} query={q} /></span>
                            <Count n={d.teams.reduce((n, t) => n + t.members.length, 0)} />
                          </button>
                        </div>
                        {isOpen(d.division.id) && d.teams.map((t) => renderTeam(t, 46))}
                      </div>
                    ))}

                    {/* 부서 없는 팀은 부서와 같은 깊이에 — 그 조직에선 이게 최상위 단위다. */}
                    {view.looseTeams.map((t) => renderTeam(t, 24))}
                  </>
                )}

                {nothingFound && (
                  <div className="orgdir-note">‘{query.trim()}’ 와 맞는 구성원이 없습니다.</div>
                )}
              </>
            )}
        </div>
      </div>

      {active && (
        <PersonDetailPopup
          person={active}
          style={detailStyle}
          placement="anchored"
          onClose={() => setActiveId(null)}
          onNavigated={close}
        />
      )}
    </>,
    document.body,
  );
}

/** 루트에 1회 마운트하는 호스트 — App.tsx. */
export function OrgDirectoryHost() {
  return <OrgDirectoryModal />;
}
